import { isBrowser, isNode } from 'browser-or-node';
import type { Image } from 'canvas';
import type { ImageConfig, Image as KonvaImageRef } from 'konva/lib/shapes/Image';
import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Image as ImageNode } from 'react-konva';
import { ImageLibraryContext } from '../_components/ImageLibrary';

export type Props = {
    src: string
    onLoad?: () => void
    imageDensity?: number
    disableCache?: boolean
    filters?: ImageConfig['filters']
    sceneFunc?: ImageConfig['sceneFunc']
} & Omit<ImageConfig, 'image'>;


const KonvaImage = forwardRef<KonvaImageRef | null, Props>(function KonvaImage(attributes, ref) {
    const props = attributes as Props;
    const innerRef = useRef<KonvaImageRef|null>(null);
    const imageRef = useRef<Image|HTMLImageElement|null>(null);
    const [imageElem,setImageElem] = useState<Image|HTMLImageElement|null>(null);
    const imageLibrary = useContext(ImageLibraryContext)
    const bounded = isNode ? imageLibrary.bounded : undefined;

    const handleLoad = useCallback(() => {
        if(isBrowser) {
            setImageElem(imageRef.current ?? null);
        }
    }, []);

    useEffect(() => {
        if(isBrowser) {      
            const tmpImg = new window.Image();

            imageRef.current = tmpImg;

            tmpImg.crossOrigin = 'anonymous';
            tmpImg.addEventListener('load', handleLoad);
            tmpImg.src = props.src;

            return () => {
                tmpImg.removeEventListener('load', handleLoad);
                imageRef.current = null;
            }
        }
    }, [props.src, handleLoad]);

    if(isNode && bounded === undefined) {
        imageRef.current = imageLibrary.retrieveImage(props.src) ?? null;
    }
    
    useEffect(() => {
        if (bounded !== undefined) return;
        if (isBrowser && imageElem === null) {
            return;
        }

        props.onLoad?.();
        
        if(innerRef.current !== null) {
            innerRef.current.clearCache();
            if(!props.disableCache && innerRef.current.width() > 0 && innerRef.current.height() > 0) {
                // Layer dimensions describe placement; retain the loaded preview's resolution in the browser cache.
                const pixelRatio = isBrowser && imageElem !== null
                    ? Math.min(imageElem.width / innerRef.current.width(), imageElem.height / innerRef.current.height())
                    : props.imageDensity;
                innerRef.current.cache({ pixelRatio });
            }
        }
    }, [imageElem, props.width, props.height, props.imageDensity, props.disableCache, props.onLoad, bounded])

    useImperativeHandle<KonvaImageRef | null, KonvaImageRef | null>(ref, () => innerRef.current, [])

    const {src, image, ...otherProps} = props;

    return <ImageNode ref={innerRef}
        image={(isNode ? imageRef.current : imageElem) as unknown as CanvasImageSource | undefined}
        {...otherProps}
        sceneFunc={bounded === undefined ? props.sceneFunc : (context, shape) => {
            if (props.filters !== undefined && props.filters.length > 0 && props.disableCache !== true) {
                const filters = props.filters.map(filter => {
                    if (typeof filter !== 'function') throw new Error('Unsupported native pixel filter');
                    return filter;
                });
                bounded.drawFiltered(context._context, src, shape.width(), shape.height(), props.imageDensity ?? 1, filters, shape);
            } else {
                bounded.draw(context._context, src, 0, 0, shape.width(), shape.height(), shape);
            }
        }}
    />
});

export default KonvaImage;
